import useMount from 'react-use/esm/useMount';
import analytics from 'modules/analytics';
import { globalContext } from 'modules/analytics/constants';
// example values provided for Macbook M2
interface SystemInfo {
  webgpu: {
    // ex 'metal-3', 'vulkan-1'
    architecture: string;
    // ex 'apple', 'nvidia', 'intel'
    vendor: string;
    supported: boolean;
    // VRAM proxy signals — analytics-only, not used for gating.
    // ex. 268435456
    maxBufferSize: number | null;
    // ex. 134217728
    maxStorageBufferBindingSize: number | null;
  };
  webgl: {
    // ex. ANGLE (Apple, ANGLE Metal Renderer: Apple M1 Max, Unspecified Version)
    renderer: string;
    // ex. Google Inc. (Apple)
    vendor: string;
    // ex. WebGL 1.0 (OpenGL ES 2.0 Chromium)
    version: string;
    experimental: boolean;
    // VRAM proxy signals — analytics-only, not used for gating.
    // ex. 16384
    maxTextureSize: number | null;
    // Heuristic from the renderer string: integrated GPUs share system RAM
    // and tend to underperform on heavy 3D pixel data.
    isIntegrated: boolean;
  };
  system: {
    // ex. 10
    cpuCores: string | number;
  };
}

interface GPUSupportedLimits {
  maxBufferSize?: number;
  maxStorageBufferBindingSize?: number;
}

interface GPUAdapterInfo {
  vendor?: string;
  architecture?: string;
}

interface GPUAdapter {
  info?: GPUAdapterInfo;
  limits?: GPUSupportedLimits;
}

interface GPU {
  requestAdapter(): Promise<GPUAdapter | null>;
}

interface NavigatorGPU extends Navigator {
  gpu?: GPU;
}

const INTEGRATED_RENDERER_PATTERNS: ReadonlyArray<RegExp> = [
  /\bIntel\b.*?\b(?:UHD|HD|Iris)\b/i,
  /\bAMD\b.*?\bRadeon (?:R[1-9]|Vega) Graphics\b/i,
  /\bApple M\d/i,
];

const isIntegratedRenderer = (renderer: string): boolean =>
  INTEGRATED_RENDERER_PATTERNS.some((p) => p.test(renderer));

const getCpuInfo = () => {
  const cpuCores = navigator.hardwareConcurrency ?? 'Unavailable';
  return { cpuCores };
};

const getWebGPUInfo = async () => {
  let supported = false;
  let vendor = 'Unavailable';
  let architecture = 'Unavailable';
  let maxBufferSize: number | null = null;
  let maxStorageBufferBindingSize: number | null = null;

  const castedGPUNavigator = navigator as NavigatorGPU;

  if ('gpu' in castedGPUNavigator) {
    try {
      const adapter = await castedGPUNavigator.gpu?.requestAdapter();
      if (adapter) {
        const info = adapter.info ?? null;
        supported = true;
        vendor = info?.vendor ?? 'Unavailable';
        architecture = info?.architecture ?? 'Unavailable';
        maxBufferSize = adapter.limits?.maxBufferSize ?? null;
        maxStorageBufferBindingSize = adapter.limits?.maxStorageBufferBindingSize ?? null;
      }
    } catch (err) {
      console.error('Error requesting GPU adapter:', err);
    }
  }

  return {
    architecture,
    vendor,
    supported,
    maxBufferSize,
    maxStorageBufferBindingSize,
  };
};

const getWebGLInfo = () => {
  let renderer = 'Unavailable';
  let vendor = 'Unavailable';
  let version = 'Unavailable';
  let experimental = false;
  let maxTextureSize: number | null = null;

  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl');
    const experimentalgl = canvas.getContext('experimental-webgl');

    if (gl == null && experimentalgl != null) {
      experimental = true;
    }

    const useableContext = gl ?? experimentalgl;

    if (useableContext && useableContext instanceof WebGLRenderingContext) {
      const debugInfo = useableContext.getExtension('WEBGL_debug_renderer_info');
      version = useableContext.getParameter(useableContext.VERSION);
      renderer = debugInfo
        ? useableContext.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL)
        : 'Unavailable';
      vendor = debugInfo
        ? useableContext.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL)
        : 'Unavailable';
      maxTextureSize = useableContext.getParameter(useableContext.MAX_TEXTURE_SIZE) ?? null;
      // clean up canvas
      useableContext?.getExtension('WEBGL_lose_context')?.loseContext();
      if (canvas.parentNode) {
        canvas.parentNode.removeChild(canvas);
      }
    }
  } catch (err) {
    console.error('Error collecting WebGL info:', err);
  }

  return {
    renderer,
    vendor,
    version,
    experimental,
    maxTextureSize,
    isIntegrated: typeof renderer === 'string' && isIntegratedRenderer(renderer),
  };
};

export async function collectSystemInfo(): Promise<SystemInfo> {
  const result: SystemInfo = {
    system: getCpuInfo(),
    webgpu: await getWebGPUInfo(),
    webgl: getWebGLInfo(),
  };

  return result;
}

const logSystemInfo = async () => {
  const systemInfo = await collectSystemInfo();
  analytics.addContext(globalContext.viewer.systemInfo, systemInfo);
};

export const useSystemInfoAnalytics = () => {
  useMount(() => {
    logSystemInfo();
  });
};
